/**
 * RegistrationPopup Component
 * 
 * Popup modal for helper registration with the following features:
 * - Single helper registration form
 * - Excel file import for bulk registration
 * - Success message display after registration
 * - Import success/error messages
 * 
 * @param onClose - Callback function to close the popup
 * @param onSuccess - Callback function called when a helper is successfully registered
 */
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { API_BASE_URL } from '../constants';
import { useAuth } from '../contexts/AuthContext';
import RegistrationForm from './RegistrationForm';
import SuccessMessage from './SuccessMessage';
import './RegistrationPopup.css';

interface RegistrationPopupProps {
  /** 'form' = registration form, 'excel-import' = Excel import only */
  openMode?: 'form' | 'excel-import';
  onClose: () => void;
  onSuccess: (firstName: string, surname: string) => void;
  onNavigateToHelperList?: () => void;
  /** When true, form is read-only and registration is blocked (year is read-only) */
  yearMaintenanceDisabled?: boolean;
}

const RegistrationPopup: React.FC<RegistrationPopupProps> = ({ openMode = 'form', onClose, onSuccess, onNavigateToHelperList, yearMaintenanceDisabled: yearMaintenanceDisabledProp = false }) => {
  const { isAdmin, user, token, year } = useAuth();
  const [isRegistered, setIsRegistered] = React.useState(false);
  const [registeredUser, setRegisteredUser] = React.useState<{firstName: string, surname: string} | null>(null);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(false);
  const [yearMaintenanceDisabledLocal, setYearMaintenanceDisabledLocal] = useState<boolean>(false);
  const yearMaintenanceDisabled = yearMaintenanceDisabledProp || yearMaintenanceDisabledLocal;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isExcelImportMode = openMode === 'excel-import';

  useEffect(() => {
    fetchMaintenanceStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year]);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success && response.data.years && year) {
        const currentYearData = response.data.years.find((y: { year: number; disabled: boolean }) => y.year === year);
        if (currentYearData) {
          setYearMaintenanceDisabledLocal(currentYearData.disabled || false);
        }
      }
    } catch (err) {
      console.error('Error fetching maintenance status:', err);
      setYearMaintenanceDisabledLocal(false);
    }
  };

  const handleRegistrationSuccess = (firstName: string, surname: string) => {
    setRegisteredUser({ firstName, surname });
    setIsRegistered(true);
    setHasUnsavedChanges(false);
  };

  const handleReset = () => {
    setIsRegistered(false);
    setRegisteredUser(null);
    setHasUnsavedChanges(false);
  };

  const handleClose = () => {
    if (isRegistered) {
      handleReset();
    }
    onClose();
  };

  const handleSaveAndClose = () => {
    setShowUnsavedConfirm(false);
    // Find and trigger the form submission button
    const form = document.querySelector('.registration-form') as HTMLFormElement;
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]') as HTMLButtonElement;
      if (submitButton && !submitButton.disabled) {
        submitButton.click();
      }
    }
    // Note: The form's onSuccess will handle closing after successful registration
  };

  const handleDiscardAndClose = () => {
    setShowUnsavedConfirm(false);
    handleClose();
  };

  const handleCancel = () => {
    setShowUnsavedConfirm(false);
  };

  // Handle Escape key to close dialog
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isRegistered && !showUnsavedConfirm) {
        if (hasUnsavedChanges) {
          setShowUnsavedConfirm(true);
        } else {
          handleClose();
        }
      } else if (e.key === 'Escape' && showUnsavedConfirm) {
        handleCancel();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- handleClose used intentionally
  }, [isRegistered, hasUnsavedChanges, showUnsavedConfirm]);

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (yearMaintenanceDisabled) return;

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setImporting(true);
    setImportMessage(null);

    try {
      // Read Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      
      // Get first sheet
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        setImportMessage({
          type: 'error',
          text: 'Die Excel-Datei ist leer'
        });
        setImporting(false);
        return;
      }

      // For Fachbereich Administrators, filter Fachbereiche to only their assigned ones
      let processedHelpers = jsonData;
      if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
        const userFachbereiche = user.fachbereiche;
        
        processedHelpers = jsonData.map((helper: any) => {
          // Parse Fachbereiche from various possible column names
          let fachbereiche: string[] = [];
          if (helper.fachbereiche) {
            fachbereiche = Array.isArray(helper.fachbereiche) ? helper.fachbereiche : [helper.fachbereiche];
          } else if (helper['Zuordnung zu Fachbereich'] || helper['zuordnungZuFachbereich'] || helper['zuordnung_zu_fachbereich']) {
            const fb = helper['Zuordnung zu Fachbereich'] || helper['zuordnungZuFachbereich'] || helper['zuordnung_zu_fachbereich'];
            fachbereiche = typeof fb === 'string' && fb.includes(',') ? fb.split(',').map((f: string) => f.trim()) : [fb];
          } else if (helper.fachbereich || helper['Fachbereich'] || helper['Fachbereiche']) {
            const fb = helper.fachbereich || helper['Fachbereich'] || helper['Fachbereiche'];
            fachbereiche = typeof fb === 'string' && fb.includes(',') ? fb.split(',').map((f: string) => f.trim()) : [fb];
          }

          // Filter to only user's Fachbereiche
          const originalFachbereiche = fachbereiche.filter(fb => fb && String(fb).trim()).map(fb => String(fb).trim());
          const filteredFachbereiche = originalFachbereiche.filter(fb => userFachbereiche.includes(fb));

          // Update the helper object with filtered Fachbereiche
          const updatedHelper = { ...helper };
          if (helper.fachbereiche) {
            updatedHelper.fachbereiche = filteredFachbereiche;
          } else if (helper['Zuordnung zu Fachbereich'] || helper['zuordnungZuFachbereich'] || helper['zuordnung_zu_fachbereich']) {
            updatedHelper['Zuordnung zu Fachbereich'] = filteredFachbereiche.join(', ');
          } else if (helper.fachbereich || helper['Fachbereich'] || helper['Fachbereiche']) {
            updatedHelper.fachbereich = filteredFachbereiche.join(', ');
          }

          return updatedHelper;
        });
      }

      // Send to backend
      const response = await axios.post(`${API_BASE_URL}/api/register/batch`, {
        helpers: processedHelpers
      });

      if (response.data.success) {
        const failed = response.data.failed || 0;
        let message = response.data.message || '';
        
        if (failed > 0) {
          message += `, ${failed} fehlgeschlagen`;
        }
        
        if (response.data.errors && response.data.errors.length > 0) {
          const errorCount = response.data.errors.length;
          message += `. ${errorCount} Warnung${errorCount > 1 ? 'en' : ''}:`;
          if (errorCount <= 5) {
            message += ' ' + response.data.errors.join('; ');
          } else {
            message += ' ' + response.data.errors.slice(0, 5).join('; ') + ` und ${errorCount - 5} weitere`;
          }
        }

        setImportMessage({
          type: 'success',
          text: message
        });

        // Clear message after 8 seconds
        setTimeout(() => {
          setImportMessage(null);
        }, 8000);
      } else {
        const details = response.data.details ? ` (Details: ${response.data.details})` : '';
        const errorList = response.data.errors && response.data.errors.length > 0 
          ? ` Fehler: ${response.data.errors.slice(0, 3).join('; ')}${response.data.errors.length > 3 ? '...' : ''}`
          : '';
        setImportMessage({
          type: 'error',
          text: `${response.data.message || 'Fehler beim Importieren'}${details}${errorList}`
        });
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Lesen der Excel-Datei';
      let details = '';
      
      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        if (err.response.data.details) {
          details = ` (Details: ${err.response.data.details})`;
        }
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errorList = err.response.data.errors.slice(0, 3).join('; ');
          details += ` Fehler: ${errorList}${err.response.data.errors.length > 3 ? '...' : ''}`;
        }
      } else if (err.message) {
        details = ` (${err.message})`;
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        errorMessage = 'Verbindung zum Server nicht möglich';
        details = '. Bitte stellen Sie sicher, dass der Backend-Server läuft.';
      }

      setImportMessage({
        type: 'error',
        text: `${errorMessage}${details}`
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      {/* Unsaved changes confirmation dialog */}
      {showUnsavedConfirm && (
        <div className="registration-popup-overlay" style={{ zIndex: 2000 }}>
          <div className="registration-popup-container" style={{ maxWidth: '400px' }} onClick={(e) => e.stopPropagation()}>
            <div className="registration-popup-header">
              <h2 className="registration-popup-title">Ungespeicherte Änderungen</h2>
            </div>
            <div style={{ padding: '20px' }}>
              <p style={{ marginBottom: '20px' }}>Sie haben ungespeicherte Änderungen. Was möchten Sie tun?</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <button
                  type="button"
                  className="registration-popup-submit"
                  onClick={handleSaveAndClose}
                  style={{ width: '100%' }}
                >
                  💾 Speichern
                </button>
                <button
                  type="button"
                  className="registration-popup-cancel"
                  onClick={handleCancel}
                  style={{ width: '100%' }}
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  className="registration-popup-discard"
                  onClick={handleDiscardAndClose}
                  style={{ width: '100%' }}
                >
                  Fortfahren (Änderungen verwerfen)
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="registration-popup-overlay" onClick={() => {
        if (hasUnsavedChanges && !isRegistered) {
          setShowUnsavedConfirm(true);
        } else {
          handleClose();
        }
      }}>
        <div className="registration-popup-container" onClick={(e) => e.stopPropagation()}>
          <button className="registration-popup-close" onClick={() => {
            if (hasUnsavedChanges && !isRegistered) {
              setShowUnsavedConfirm(true);
            } else {
              handleClose();
            }
          }}>×</button>
        <div className="registration-popup-header">
          <h2 className="registration-popup-title">{isExcelImportMode ? 'Helfer aus Excel importieren' : 'Helfer registrieren'}</h2>
          <div className="registration-popup-header-actions">
            {!isExcelImportMode && user?.role === 'Fachbereich Administrator' && onNavigateToHelperList && (
              <div className="registration-popup-header-actions-row1">
                <button 
                  className="registration-popup-import" 
                  onClick={() => {
                    if (!yearMaintenanceDisabled) {
                      onClose();
                      onNavigateToHelperList();
                    }
                  }}
                  disabled={yearMaintenanceDisabled}
                  title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Helfer können nicht bearbeitet werden` : "Registrierte Helfer bearbeiten"}
                  style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                >
                  ✏️ Registrierte Helfer bearbeiten
                </button>
              </div>
            )}
            {isExcelImportMode && (
              <div className="registration-popup-header-actions-row2">
                <button
                  className="registration-popup-import hide-import-on-mobile-phone"
                  onClick={handleImportClick}
                  disabled={importing || yearMaintenanceDisabled}
                  title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Import nicht möglich` : "Excel-Datei auswählen"}
                  style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                >
                  {importing ? '⏳' : '📤'} Excel-Datei auswählen
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileImport}
                  style={{ display: 'none' }}
                />
              </div>
            )}
          </div>
        </div>
        {importMessage && (
          <div className={`registration-popup-import-message ${importMessage.type}`}>
            <div className="registration-popup-import-message-text">{importMessage.text}</div>
            {importMessage.type === 'success' && onNavigateToHelperList && (
              <button
                className="registration-popup-navigate-button"
                onClick={() => {
                  onClose();
                  onNavigateToHelperList();
                }}
                title="Zur Gesamt Helfer Liste wechseln"
              >
                📋 Zur Helfer Liste
              </button>
            )}
          </div>
        )}
        <div className="registration-popup-content">
          {yearMaintenanceDisabled && (
            <div className="error-message" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px', padding: '8px 12px' }}>
              ⚠️ Das Jahr {year} ist schreibgeschützt. Neue Helfer können nicht registriert werden.
            </div>
          )}
          {isExcelImportMode ? (
            !importMessage ? (
              <p style={{ color: '#666', marginTop: '8px' }}>Wählen Sie eine Excel-Datei (.xlsx oder .xls) mit Helferdaten.</p>
            ) : null
          ) : !isRegistered ? (
            <RegistrationForm onSuccess={handleRegistrationSuccess} onFormChange={setHasUnsavedChanges} yearMaintenanceDisabled={yearMaintenanceDisabled} />
          ) : (
            <SuccessMessage 
              firstName={registeredUser?.firstName || ''} 
              surname={registeredUser?.surname || ''}
              year={year}
              onReset={handleReset}
            />
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default RegistrationPopup;

